import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, Ref } from 'react';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { CopyableId } from '../../components/CopyableId';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { actionErrorMessage } from '../_shared/actionError';
import { formatTimestampUs, shortId } from '../../lib/format';
import '../_shared/shared.css';
import type { AdminOrgDetail, AdminOrgMember, AdminOrgSummary, AssignableOrgRole } from './organizationsApi';
import { addOrganizationMember, getOrganization, listOrganizations, removeOrganizationMember, updateOrganizationMemberRole } from './organizationsApi';
import { ProductionGrantPanel } from './ProductionGrantPanel';
import { PRODUCTION_GRANT_STATE_LABELS, productionGrantTone } from './productionGrantApi';
import './organizations.css';

const ORG_STATES = [
  { value: '', label: '全部狀態' },
  { value: 'approved', label: '已核准' },
  { value: 'pending', label: '待審核' },
  { value: 'rejected', label: '已拒絕' },
  { value: 'suspended', label: '已停權' },
];

const ASSIGNABLE_ROLES: Array<{ value: AssignableOrgRole; label: string }> = [
  { value: 'admin', label: '管理員' },
  { value: 'developer', label: '開發者' },
  { value: 'viewer', label: '檢視者' },
];

// 對照表全中文化;未知列舉值不直接印後端原始字串,固定顯示「其他狀態」、
// 原始值只留在呼叫端的 title attribute 供除錯(避免術語外洩)。

/** 登記資訊的完整 tooltip:有什麼顯示什麼,不編造缺的欄位。 */
function companyInfoTitle(info: { name?: string; tax_id?: string; country?: string; contact?: string }): string {
  return [
    info.name ? `登記名:${info.name}` : '',
    info.tax_id ? `統編/稅籍:${info.tax_id}` : '',
    info.country ? `國別:${info.country}` : '',
    info.contact ? `聯絡:${info.contact}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

function stateLabel(state: string | undefined): string {
  if (!state) return '其他狀態';
  const labels: Record<string, string> = {
    approved: '已核准',
    pending: '待審核',
    applied: '待審核',
    rejected: '已拒絕',
    suspended: '已停權',
  };
  return labels[state] ?? '其他狀態';
}

function stateTone(state: string | undefined): string {
  switch (state) {
    case 'approved':
      return 'good';
    case 'pending':
    case 'applied':
      return 'warn';
    case 'rejected':
    case 'suspended':
      return 'danger';
    default:
      return 'neutral';
  }
}

function roleLabel(role: string | undefined): string {
  if (!role) return '—';
  const labels: Record<string, string> = {
    owner: '擁有者',
    admin: '管理員',
    developer: '開發者',
    viewer: '檢視者',
  };
  return labels[role] ?? '其他角色';
}

function formatInviteExpiry(seconds: number | undefined): string {
  if (!seconds) return '—';
  return formatTimestampUs(seconds * 1_000_000, { second: undefined });
}

export default function OrganizationsPage() {
  const [state, setState] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<AdminOrgSummary[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminOrgDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<unknown>(null);
  const [detailNonce, setDetailNonce] = useState(0);
  // 選列後把下方詳情 workbench 捲進視野;初次自動選取(非使用者點擊)不捲動。
  const detailRef = useRef<HTMLElement>(null);
  const shouldScrollRef = useRef(false);

  function handleSelect(orgId: string) {
    if (orgId === selectedId) return;
    shouldScrollRef.current = true;
    setSelectedId(orgId);
  }

  useEffect(() => {
    if (shouldScrollRef.current && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      shouldScrollRef.current = false;
    }
  }, [selectedId, detail]);

  async function load(cursor = '', stack = cursorStack) {
    setLoading(true);
    setError(null);
    try {
      const page = await listOrganizations({ state, query, cursor, limit: 50 });
      setItems(page.items);
      setNextCursor(page.nextCursor);
      setCurrentCursor(cursor);
      setCursorStack(stack);
      if (page.items.length > 0 && (!selectedId || !page.items.some((org) => org.org_id === selectedId))) {
        setSelectedId(page.items[0].org_id);
      }
      if (page.items.length === 0) {
        setSelectedId(null);
        setDetail(null);
      }
    } catch (err) {
      setError(err);
      setItems(null);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load('', []);
  }, [state, query]);

  useEffect(() => {
    let alive = true;
    if (!selectedId) return undefined;
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    getOrganization(selectedId)
      .then((org) => {
        if (alive) setDetail(org);
      })
      .catch((err) => {
        if (alive) setDetailError(err);
      })
      .finally(() => {
        if (alive) setDetailLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [selectedId, detailNonce]);

  function submitSearch(e: FormEvent) {
    e.preventDefault();
    setQuery(queryInput.trim());
  }

  function clearSearch() {
    setQueryInput('');
    setQuery('');
  }

  function nextPage() {
    if (!nextCursor) return;
    void load(nextCursor, [...cursorStack, currentCursor]);
  }

  function prevPage() {
    if (cursorStack.length === 0) return;
    const nextStack = cursorStack.slice(0, -1);
    const cursor = cursorStack[cursorStack.length - 1] ?? '';
    void load(cursor, nextStack);
  }

  function refreshAfterMutation() {
    setDetailNonce((n) => n + 1);
    void load(currentCursor, cursorStack);
  }

  const selectedSummary = useMemo(() => items?.find((org) => org.org_id === selectedId) ?? null, [items, selectedId]);

  // 頁級 KPI:由本頁載入的組織依 stateTone 分桶(與 companies / customerFamilies 頁對齊)。
  const kpi = useMemo(() => {
    const list = items ?? [];
    let approved = 0;
    let pending = 0;
    let blocked = 0;
    for (const org of list) {
      const tone = stateTone(org.state);
      if (tone === 'good') approved += 1;
      else if (tone === 'warn') pending += 1;
      else if (tone === 'danger') blocked += 1;
    }
    return { total: list.length, approved, pending, blocked };
  }, [items]);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">開發者組織</h1>
          <p className="page-subtitle">平台層開發者組織、擁有者、成員與待處理邀請。</p>
        </div>
        <button className="btn-ghost btn-sm" onClick={() => void load(currentCursor, cursorStack)} disabled={loading}>
          {loading ? '載入中…' : '重新整理'}
        </button>
      </div>

      {/* 頁級 KPI 摘要條:與鄰頁對齊,主格「組織總數」+ 依狀態分桶的副格 */}
      {items && (
        <div className="kpi-strip devorg-kpi-strip">
          <div className="kpi-card kpi-card--hero">
            <span className="kpi-label">本頁組織</span>
            <span className="kpi-value">{kpi.total}{nextCursor ? '+' : ''}</span>
            <span className="kpi-foot">{nextCursor ? '本頁載入筆數(尚有更多頁,非全站總數)' : '本頁載入的開發者組織筆數'}</span>
          </div>
          <div className="kpi-card">
            <span className="kpi-label">已核准</span>
            <span className="kpi-value">{kpi.approved}</span>
            <span className="kpi-foot">狀態為已核准</span>
          </div>
          <div className="kpi-card">
            <span className="kpi-label">待審核</span>
            <span className="kpi-value">{kpi.pending}</span>
            <span className="kpi-foot">狀態為待審核</span>
          </div>
          <div className="kpi-card kpi-card--secondary">
            <span className="kpi-label">已停權 / 已拒絕</span>
            <span className="kpi-value">{kpi.blocked}</span>
            <span className="kpi-foot">已停權或已拒絕</span>
          </div>
        </div>
      )}

      <div className="toolbar">
        <form className="adm-toolbar-form" onSubmit={submitSearch} role="search">
          <label className="adm-field devorg-search-field">
            <span className="text-tertiary adm-field-label">搜尋組織 / 識別碼</span>
            <input
              type="search"
              placeholder="living-style"
              aria-label="搜尋組織"
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
            />
          </label>
          <button type="submit" className="btn-primary" disabled={loading}>
            查詢
          </button>
          {(query || queryInput) && (
            <button type="button" className="btn-ghost" onClick={clearSearch} disabled={loading}>
              清除
            </button>
          )}
          <span className="adm-toolbar-spacer" />
          <label className="adm-field">
            <span className="text-tertiary adm-field-label">狀態</span>
            <select value={state} onChange={(e) => setState(e.target.value)} disabled={loading}>
              {ORG_STATES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        </form>
      </div>

      {loading && !items && (
        <div className="card">
          <LoadingState label="載入組織清單…" />
        </div>
      )}

      {error != null && !loading && <ErrorState error={error} onRetry={() => void load(currentCursor, cursorStack)} />}

      {error == null && items && items.length === 0 && (
        <EmptyState title="沒有符合條件的組織" detail="調整狀態或搜尋條件後再查詢。" />
      )}

      {error == null && items && items.length > 0 && (
        <div className="devorg-layout">
          <section className="card devorg-directory">
            <div className="section-head">
              <h2 className="section-title">組織目錄</h2>
              <span className="section-meta">
                {query ? `搜尋「${query}」· ` : ''}第 {cursorStack.length + 1} 頁 · 本頁 {items.length} 筆
              </span>
            </div>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>組織</th>
                    <th>狀態</th>
                    <th>生產授權</th>
                    <th>擁有者</th>
                    <th>成員</th>
                    <th>邀請</th>
                    <th>更新時間</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((org) => (
                    <OrgRow key={org.org_id} org={org} active={org.org_id === selectedId} onSelect={handleSelect} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* 分頁資訊整合為一列:第 N 頁 + 本頁筆數 + 上下頁按鈕,不再兩處重複 */}
            <div className="adm-pager">
              <button className="btn-ghost btn-sm" onClick={prevPage} disabled={cursorStack.length === 0 || loading}>
                ← 上一頁
              </button>
              <span className="text-tertiary">
                第 {cursorStack.length + 1} 頁 · 本頁 {items.length} 筆
              </span>
              <button className="btn-ghost btn-sm" onClick={nextPage} disabled={!nextCursor || loading}>
                下一頁 →
              </button>
            </div>
          </section>

          <OrgDetailPanel
            panelRef={detailRef}
            org={detail}
            summary={selectedSummary}
            loading={detailLoading}
            error={detailError}
            onRetry={() => setDetailNonce((n) => n + 1)}
            onMutated={refreshAfterMutation}
          />
        </div>
      )}
    </>
  );
}

function OrgRow({
  org,
  active,
  onSelect,
}: {
  org: AdminOrgSummary;
  active: boolean;
  onSelect: (orgId: string) => void;
}) {
  return (
    <tr
      className={`clickable ${active ? 'devorg-row-active' : ''}`}
      tabIndex={0}
      onClick={() => onSelect(org.org_id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSelect(org.org_id);
      }}
    >
      <td>
        <div className="adm-cell-strong devorg-text-clip" title={org.name}>
          {org.name || '—'}
        </div>
        {/* 公司登記名(S16)。與 org.name 分開顯示:org.name 是自取的顯示名,
            company_info.name 是核可時鎖定的登記名 —— 稽核與對帳看的是後者,
            兩者不一致本身就是要看得見的訊號。 */}
        {org.company_info?.name ? (
          <div className="text-tertiary devorg-text-clip" title={companyInfoTitle(org.company_info)}>
            登記名:{org.company_info.name}
          </div>
        ) : null}
        <div className="adm-mono text-tertiary">
          <CopyableId value={org.org_id} display={org.slug || shortId(org.org_id)} className="devorg-text-clip" />
        </div>
      </td>
      <td>
        <span className={`pill pill-dot pill-${stateTone(org.state)}`} title={org.state}>
          {stateLabel(org.state)}
        </span>
      </td>
      <td>
        {org.production_grant ? (
          <span
            className={`pill pill-dot pill-${productionGrantTone(org.production_grant.state)}`}
            title={`Production Grant v${org.production_grant.version}`}
          >
            {PRODUCTION_GRANT_STATE_LABELS[org.production_grant.state]}
          </span>
        ) : (
          <span className="text-tertiary">—</span>
        )}
      </td>
      <td>
        <div className="devorg-text-clip" title={org.owner_email || org.owner_user_id}>
          {org.owner_email || '—'}
        </div>
        <div className="adm-mono text-tertiary">
          <CopyableId value={org.owner_user_id} display={shortId(org.owner_user_id)} className="devorg-text-clip" />
        </div>
      </td>
      <td>{org.member_count ?? 0}</td>
      <td>{org.pending_invites ?? 0}</td>
      <td className="adm-mono">{formatTimestampUs(org.updated_at || org.created_at, { second: undefined })}</td>
    </tr>
  );
}

function OrgDetailPanel({
  panelRef,
  org,
  summary,
  loading,
  error,
  onRetry,
  onMutated,
}: {
  panelRef: Ref<HTMLElement>;
  org: AdminOrgDetail | null;
  summary: AdminOrgSummary | null;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  onMutated: () => void;
}) {
  const display = org ?? summary;
  // accent 色調沿用 stateTone 語意色,未知狀態(neutral)退回品牌 teal 而非灰色
  const rawTone = display?.state ? stateTone(display.state) : 'neutral';
  const accentTone = rawTone === 'neutral' ? 'brand' : rawTone;
  return (
    <section className="card devorg-workbench" aria-label="組織詳情" ref={panelRef}>
      {/* 狀態 accent 條:取代原本縮在角落的小 pill,作為詳情區單一視覺焦點 */}
      <div className={`adm-detail-accent tone-${accentTone}`} />
      <div className="devorg-detail-head">
        <div className="devorg-detail-headline">
          <h2 className="devorg-detail-title">{display?.name ?? '組織'}</h2>
          <div className="adm-mono text-tertiary">
            {display?.org_id ? <CopyableId value={display.org_id} display={shortId(display.org_id, 14, 6)} className="devorg-text-clip" /> : '—'}
          </div>
        </div>
        {display?.state && (
          <span className={`devorg-detail-pill pill pill-dot pill-${stateTone(display.state)}`} title={display.state}>
            {stateLabel(display.state)}
          </span>
        )}
      </div>

      {loading && <LoadingState label="載入組織詳情…" />}
      {!loading && error != null && <ErrorState error={error} onRetry={onRetry} />}
      {!loading && error == null && !display && <EmptyState title="尚未選取組織" detail="從上方目錄點選一個組織以檢視成員與邀請。" />}
      {!loading && error == null && display && (
        <div className="devorg-detail-body">
          <div className="devorg-summary">
            <dl className="adm-kv devorg-kv-muted">
              <dt>代稱</dt>
              <dd className="adm-mono">
                {display.slug ? <CopyableId value={display.slug} display={display.slug} className="devorg-text-clip" /> : '—'}
              </dd>
              <dt>擁有者</dt>
              <dd>
                {display.owner_email || (display.owner_user_id ? <CopyableId value={display.owner_user_id} display={shortId(display.owner_user_id)} className="devorg-text-clip" /> : '—')}
              </dd>
              <dt>預設生態系</dt>
              <dd className="adm-mono">{display.default_family_id || '尚未設定'}</dd>
              <dt>建立時間</dt>
              <dd className="adm-mono">{formatTimestampUs(display.created_at, { second: undefined })}</dd>
              <dt>更新時間</dt>
              <dd className="adm-mono">{formatTimestampUs(display.updated_at, { second: undefined })}</dd>
            </dl>

            <div className="devorg-stat-row">
              <div className="devorg-stat">
                <span className="devorg-stat-value">{display.member_count ?? 0}</span>
                <span className="devorg-stat-label">成員數</span>
              </div>
              <div className="devorg-stat">
                <span className="devorg-stat-value">{display.pending_invites ?? 0}</span>
                <span className="devorg-stat-label">待處理邀請</span>
              </div>
              <div className="devorg-stat">
                <span className="devorg-stat-value">{display.quota_overrides ? Object.keys(display.quota_overrides).length : 0}</span>
                <span className="devorg-stat-label">配額覆寫</span>
              </div>
            </div>
          </div>

          {org && <ProductionGrantPanel org={org} onMutated={onMutated} />}
          {org && <RosterTables org={org} onMutated={onMutated} />}
        </div>
      )}
    </section>
  );
}

function assignableRoleOf(role: string | undefined): AssignableOrgRole {
  if (role === 'admin' || role === 'developer' || role === 'viewer') return role;
  return 'viewer';
}

function RosterTables({ org, onMutated }: { org: AdminOrgDetail; onMutated: () => void }) {
  const { role } = useAuth();
  const writable = canAdminWrite(role); // cs-readonly 只讀:成員新增/改角色/移除全數收起
  const [email, setEmail] = useState('');
  const [newRole, setNewRole] = useState<AssignableOrgRole>('developer');
  const [reason, setReason] = useState('');
  const [caseId, setCaseId] = useState('');
  const [roleDrafts, setRoleDrafts] = useState<Record<string, AssignableOrgRole>>({});
  // 記錄「哪一列的角色正在 inline 編輯」;其餘列一律顯示唯讀角色 pill。
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AdminOrgMember | null>(null);

  const auditContext = {
    reason: reason.trim() || undefined,
    case_id: caseId.trim() || undefined,
  };
  // 治理域高門檻:新增 / 改角色 / 移除的原因與案件編號皆為必填(純前端 gate,不動 API)。
  const auditReady = reason.trim() !== '' && caseId.trim() !== '';

  async function submitAdd(e: FormEvent) {
    e.preventDefault();
    const cleanEmail = email.trim();
    if (!cleanEmail) return;
    setBusyKey('add');
    setActionError(null);
    try {
      await addOrganizationMember(org.org_id, { email: cleanEmail, org_role: newRole, ...auditContext });
      setEmail('');
      onMutated();
    } catch (err) {
      setActionError(actionErrorMessage(err));
    } finally {
      setBusyKey(null);
    }
  }

  async function submitRoleUpdate(member: AdminOrgMember) {
    const nextRole = roleDrafts[member.user_id] ?? assignableRoleOf(member.org_role);
    setBusyKey(`role:${member.user_id}`);
    setActionError(null);
    try {
      await updateOrganizationMemberRole(org.org_id, member.user_id, { org_role: nextRole, ...auditContext });
      setRoleDrafts((drafts) => {
        const next = { ...drafts };
        delete next[member.user_id];
        return next;
      });
      setEditingRoleId((cur) => (cur === member.user_id ? null : cur));
      onMutated();
    } catch (err) {
      setActionError(actionErrorMessage(err));
    } finally {
      setBusyKey(null);
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    setBusyKey(`remove:${removeTarget.user_id}`);
    setActionError(null);
    try {
      await removeOrganizationMember(org.org_id, removeTarget.user_id, auditContext);
      setRemoveTarget(null);
      onMutated();
    } catch (err) {
      setActionError(actionErrorMessage(err));
    } finally {
      setBusyKey(null);
    }
  }

  const memberCount = org.members.length;
  const inviteCount = org.invites.length;
  const rosterEmpty = memberCount === 0 && inviteCount === 0;

  return (
    <>
      <section className="devorg-roster">
        <div className="section-head">
          <h3 className="section-title">成員與邀請</h3>
          <span className="section-meta">
            {memberCount} 位成員{inviteCount > 0 ? ` · ${inviteCount} 筆待接受邀請` : ''}
          </span>
        </div>

        {/* 新增成員:欄位依填寫順序排列(email → 角色 → 原因 → 案件 → 送出)。唯讀角色僅檢視成員清單。 */}
        {writable ? (
          <>
            <form className="devorg-member-form" onSubmit={submitAdd}>
              <label className="adm-field devorg-member-email">
                <span className="text-tertiary adm-field-label">電子郵件</span>
                <input
                  type="email"
                  placeholder="developer@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busyKey !== null}
                />
              </label>
              <label className="adm-field">
                <span className="text-tertiary adm-field-label">角色</span>
                <select value={newRole} onChange={(e) => setNewRole(e.target.value as AssignableOrgRole)} disabled={busyKey !== null}>
                  {ASSIGNABLE_ROLES.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="adm-field">
                <span className="text-tertiary adm-field-label">原因</span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={busyKey !== null} placeholder="稽核必填,如:新增外包開發者" />
              </label>
              <label className="adm-field">
                <span className="text-tertiary adm-field-label">案件編號</span>
                <input value={caseId} onChange={(e) => setCaseId(e.target.value)} disabled={busyKey !== null} placeholder="CS-…" />
              </label>
              <button type="submit" className="btn-primary devorg-member-submit" disabled={busyKey !== null || email.trim() === '' || !auditReady}>
                {busyKey === 'add' ? '新增中…' : '新增成員'}
              </button>
            </form>
            <p className="text-tertiary devorg-form-hint">填寫電子郵件與角色即可新增;原因與案件編號為稽核必填,同時套用於改角色與移除。</p>
          </>
        ) : (
          <p className="text-tertiary devorg-form-hint">唯讀檢視:僅可查看成員與邀請,成員新增 / 改角色 / 移除需具寫入權限的管理角色。</p>
        )}
        {actionError && <p className="error-text devorg-action-error">{actionError}</p>}

        {rosterEmpty ? (
          <div className="adm-inline-empty">
            <span className="adm-inline-empty-icon">👤</span>
            <span>尚無成員,於上方新增第一位。</span>
          </div>
        ) : (
          <div className="table-scroll">
            {/* 成員與邀請合併為單一清單,以「狀態」欄區分已加入 / 邀請中,不再分兩區。 */}
            <table className="data-table devorg-members-table">
              <thead>
                <tr>
                  <th>電子郵件</th>
                  <th className="devorg-col-role">角色</th>
                  <th className="devorg-col-status">狀態</th>
                  <th>時間</th>
                  <th className="devorg-col-actions">操作</th>
                </tr>
              </thead>
              <tbody>
                {org.members.map((m) => {
                  const isOwner = m.org_role === 'owner';
                  const draftRole = roleDrafts[m.user_id] ?? assignableRoleOf(m.org_role);
                  const roleChanged = !isOwner && draftRole !== m.org_role;
                  return (
                    <tr key={m.user_id}>
                      <td>
                        <div className="devorg-text-clip" title={m.email || m.user_id}>
                          {m.email || m.display_name || '—'}
                        </div>
                        <div className="adm-mono text-tertiary">{shortId(m.user_id)}</div>
                      </td>
                      <td className="devorg-col-role">
                        {isOwner ? (
                          <span className="pill pill-neutral">{roleLabel(m.org_role)}</span>
                        ) : editingRoleId === m.user_id ? (
                          // inline-edit 模式:展開 select + 儲存 / 取消
                          <div className="devorg-role-edit">
                            <select
                              className="devorg-role-select"
                              value={draftRole}
                              onChange={(e) => setRoleDrafts((drafts) => ({ ...drafts, [m.user_id]: e.target.value as AssignableOrgRole }))}
                              disabled={busyKey !== null}
                              aria-label={`${m.email || m.user_id} 角色`}
                            >
                              {ASSIGNABLE_ROLES.map((role) => (
                                <option key={role.value} value={role.value}>
                                  {role.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="btn-primary btn-sm"
                              disabled={busyKey !== null || !roleChanged || !auditReady}
                              onClick={() => void submitRoleUpdate(m)}
                            >
                              {busyKey === `role:${m.user_id}` ? '更新中…' : '儲存'}
                            </button>
                            <button
                              type="button"
                              className="btn-ghost btn-sm"
                              disabled={busyKey === `role:${m.user_id}`}
                              onClick={() => {
                                setEditingRoleId(null);
                                setRoleDrafts((drafts) => {
                                  const next = { ...drafts };
                                  delete next[m.user_id];
                                  return next;
                                });
                              }}
                            >
                              取消
                            </button>
                          </div>
                        ) : (
                          // 讀取模式(預設):唯讀角色 pill,hover / 聚焦才浮現「更新角色」觸發鍵(僅寫入角色)
                          <div className="devorg-role-read">
                            <span className="pill pill-neutral">{roleLabel(m.org_role)}</span>
                            {writable && (
                              <button
                                type="button"
                                className="btn-ghost btn-sm devorg-role-edit-trigger"
                                disabled={busyKey !== null}
                                onClick={() => {
                                  setActionError(null);
                                  setEditingRoleId(m.user_id);
                                }}
                              >
                                更新角色
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="devorg-col-status">
                        <span className="pill pill-good pill-dot">已加入</span>
                      </td>
                      <td className="adm-mono adm-nowrap">{formatTimestampUs(m.joined_at, { second: undefined })}</td>
                      <td className="devorg-col-actions">
                        {!isOwner && writable ? (
                          <div className="devorg-row-actions">
                            <button
                              type="button"
                              className="btn-danger btn-sm"
                              disabled={busyKey !== null}
                              onClick={() => {
                                setActionError(null);
                                setRemoveTarget(m);
                              }}
                            >
                              移除
                            </button>
                          </div>
                        ) : (
                          <span className="text-tertiary">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {org.invites.map((inv) => (
                  <tr key={`invite:${inv.invite_email}:${inv.invited_at}`} className="devorg-invite-row">
                    <td>
                      <div className="devorg-text-clip" title={inv.invite_email}>
                        {inv.invite_email}
                      </div>
                      <div className="text-tertiary">尚未加入</div>
                    </td>
                    <td className="devorg-col-role">{roleLabel(inv.org_role)}</td>
                    <td className="devorg-col-status">
                      <span className="pill pill-warn pill-dot" title={`到期 ${formatInviteExpiry(inv.invite_expires_at_s)}`}>
                        邀請中
                      </span>
                    </td>
                    <td className="adm-mono adm-nowrap text-tertiary">到期 {formatInviteExpiry(inv.invite_expires_at_s)}</td>
                    <td className="devorg-col-actions text-tertiary">—</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {removeTarget && (
        <ConfirmDialog
          title="移除組織成員"
          tone="danger"
          confirmLabel="移除"
          busy={busyKey === `remove:${removeTarget.user_id}`}
          confirmDisabled={!auditReady}
          error={actionError}
          body={
            <>
              <p className="devorg-confirm-lead">
                將從「{org.name}」移除 {removeTarget.email || removeTarget.user_id} 的組織成員資格。這不會刪除該使用者帳號。
              </p>
              {!auditReady && (
                <p className="text-tertiary devorg-confirm-note">
                  請先取消,於上方成員表單填寫原因與案件編號(稽核必填)後再移除。
                </p>
              )}
            </>
          }
          onConfirm={() => void confirmRemove()}
          onCancel={() => {
            setRemoveTarget(null);
            setActionError(null);
          }}
        />
      )}
    </>
  );
}
